<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <!-- Label -->
      <label v-if="label" :for="id" class="label-text">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </label>

      <!-- Cascade Select -->
      <CascadeSelect
        :id="id"
        v-model="localValue"
        :options="options"
        :placeholder="placeholder"
        :disabled="disabled"
        :class="[
          {
            'p-invalid': isInvalid,
            'p-disabled': disabled,
            'p-inputtext-sm': size === 'small',
            'p-inputtext-lg': size === 'large'
          }
        ]"
        :optionLabel="optionLabel"
        :optionValue="optionValue"
        :optionGroupLabel="optionGroupLabel"
        :optionGroupChildren="optionGroupChildren"
        @change="onChange"
      />
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small
        v-for="(error, index) in localizedErrors"
        :key="index"
        :id="`${id}-error-${index}`"
        class="p-error block"
      >
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { MOPCascadeSelectProps } from './types'

export default defineComponent({
  name: 'MOPCascadeSelect',
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import CascadeSelect from 'primevue/cascadeselect'
import type { NestedOption } from './types'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'

const props = withDefaults(defineProps<MOPCascadeSelectProps>(), {
  modelValue: null,
  options: () => [],
  placeholder: 'Select',
  disabled: false,
  inputId: undefined,
  ariaLabelledby: undefined,
  appendTo: undefined,
  panelClass: undefined,
  loading: false,
  loadingIcon: 'pi pi-spinner pi-spin',
  required: false,
  invalid: false,
  id: () => `mop-cascade-${Math.random().toString(36).substr(2, 9)}`,
  name: undefined,
  label: '',
  helperText: '',
  showErrors: true,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  formContext: true,
  size: undefined,
  variant: 'outlined',
  labelStyle: 'fixed',
  optionLabel: 'name',
  optionValue: 'name',
  optionDisabled: 'disabled',
  optionGroupLabel: 'name',
  optionGroupChildren: 'items'
})

const emit = defineEmits<{
  'update:modelValue': [value: any]
  change: [event: any]
  'validation-error': [errors: string[]]
  'validation-success': []
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const localValue = ref(props.modelValue)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

// Event Handlers
const onChange = (event: any) => {
  emit('update:modelValue', event.value)
  emit('change', event)

  if (formState && props.name) {
    formState.setFieldValue(props.name, event.value)
  }

  isDirty.value = true
  if (props.validateOnChange) {
    validate()
  }
}

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

// Watch for model value changes
watch(() => props.modelValue, (newValue) => {
  localValue.value = newValue
})

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

defineExpose({
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>
